import { v4 as uuidv4 } from 'uuid'

/**
 * TMR WebSocket Factory.
 *
 * @example
 *   let websocket = new TmrWebSocket();
 *   websocket.start('ws://www.example.com/', message => ...);
 *
 * @alias TmrWebSocket
 * @type {ngFactory}
 */
function TmrWebSocketFactory($websocket, $log, $interval) {
  'ngInject'

  class TmrWebSocket {
    static stopAll() {
      return this.websockets.forEach(websocket => websocket.stop())
    }

    /**
     * Starts the websocket
     *
     * @param {string} url The connection url
     * @param {function} onMessageCallback Message event callback, called with the decoded message passed as parameter.
     */
    start(url, onMessageCallback) {
      if (typeof onMessageCallback !== 'function') {
        throw new Error(
          'tmrWebSocket.start() requires an onMessageCallback argument'
        )
      }
      if (this.stream !== undefined) {
        throw new Error('Trying to start a websocket that was already started')
      }

      this.stream = $websocket(url, null, {
        maxTimeout: 3000,
        reconnectIfNotNormalClose: true,
      })

      TmrWebSocket.websockets.push(this)

      // Overrides angular-websocket's close to force status 1000.
      this.stream.close = function (force) {
        if (force || !this.socket.bufferedAmount) this.socket.close(1000)
        return this
      }

      // Message may be undefined if sent from keepalive when mocking (don't know why).
      this.stream.onMessage(message => {
        if (message !== undefined) this.onMessage(message, onMessageCallback)
      })

      // @todo Better handling of connection errors.
      this.stream.onClose(event => $log.error(event))
      this.stream.onError(event => $log.error(event))

      // Pings the device manager every 5 seconds to keep the connection open.
      this.keepalive = $interval(() => this.stream.send({}), 5000)
    }

    /**
     * Event callback for websocket stream onMessage.
     *
     * @param {object} message The message object received by the websocket
     * @param {string} message.data The message JSON
     * @param {function} onMessageCallback Message event callback, called with the decoded message passed as parameter.
     */
    onMessage({ data }, onMessageCallback) {
      // Relay message to subscribed resource.
      onMessageCallback({ data: JSON.parse(data) })
    }

    /**
     * Stops the websocket
     */
    stop() {
      if (this.stream !== undefined) {
        this.stream.close(true)
        $interval.cancel(this.keepalive)
        delete this.stream
      }
    }
  }

  TmrWebSocket.websockets = []

  /**
   * An unique global session identifier
   *
   * This session is used by the Device Manager for debugging.
   * It is only stored in memory; doesn't persist between reloads.
   */
  TmrWebSocket.uuid = uuidv4()

  return TmrWebSocket
}

/**
 * TMR WebSocket run. Stops all websockets before closing window.
 *
 * @type {ngRun}
 */
let tmrWebSocketRun = /*@ngInject*/ ($window, TmrWebSocket) =>
  $window.addEventListener('beforeunload', () => TmrWebSocket.stopAll())

export const factory = ['TmrWebSocket', TmrWebSocketFactory]
export { tmrWebSocketRun as run }
